import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, BellRing, CheckCheck, MessageSquare, AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';
import { useToast } from '@/contexts/ToastContext';
import { parseSimpleMarkdown, formatDate } from '@/utils/formatters';

// Ícones por tipo de notificação
const NOTIFICATION_ICONS = {
  mencao: MessageSquare,
  atividade: Info,
  sistema: Info,
  alerta: AlertTriangle,
  sucesso: CheckCircle,
  erro: XCircle
};

// Cores por tipo
const NOTIFICATION_COLORS = {
  mencao: 'var(--color-primary)',
  atividade: 'var(--color-info)',
  sistema: 'var(--color-info)',
  alerta: 'var(--color-warning)',
  sucesso: 'var(--color-success)',
  erro: 'var(--color-danger)'
};

/**
 * NotificationCenter - Sino de notificações com dropdown
 *
 * Features:
 * - Badge com contador de não lidas
 * - Panel com lista de notificações
 * - Marcar como lido/não lido
 * - Eliminar notificações
 * - Realtime updates
 */
interface NotificationCenterProps {
  isExpanded?: boolean;
  onOpenDrawer?: (type: string, id: string) => void;
}

export default function NotificationCenter({ isExpanded = true, onOpenDrawer }: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isBellHovered, setIsBellHovered] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { showInfo } = useToast();

  // Refs para evitar stale closures no toast (que é criado antes das funções estarem disponíveis)
  const onOpenDrawerRef = useRef(onOpenDrawer);
  const markAsReadRef = useRef<((id: string) => void) | null>(null);

  useEffect(() => {
    onOpenDrawerRef.current = onOpenDrawer;
  }, [onOpenDrawer]);

  // Callback para mostrar toast quando nova notificação chegar
  const handleNewNotification = useCallback((notification: Record<string, any>) => {
    // Mostrar toast com onClick para abrir drawer E marcar como lida
    showInfo(
      {
        title: notification.titulo,
        description: notification.mensagem
      },
      {
        duration: 8000,
        onClick: () => {
          // Marcar notificação como lida
          markAsReadRef.current?.(notification.id);
          // Abrir drawer da entidade sem navegar
          if (notification.entidade_tipo && notification.entidade_id) {
            onOpenDrawerRef.current?.(notification.entidade_tipo, notification.entidade_id);
          }
        }
      }
    );
  }, [showInfo]);

  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead
  } = useNotifications(handleNewNotification);

  // Atualizar ref do markAsRead quando disponível
  useEffect(() => {
    markAsReadRef.current = markAsRead;
  }, [markAsRead]);

  // Calcular posição do dropdown
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.right + 12
      });
    }
  }, [isOpen]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // P3-006: Escape closes the popover (a11y).
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Formatar data relativa
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'agora';
    if (diffMins < 60) return `${diffMins} min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    return formatDate(dateString);
  };

  // Renderizar texto com suporte a **negrito**
  const renderMarkdown = (text: string) => {
    const segments = parseSimpleMarkdown(text);
    if (!segments) return text;
    return segments.map((seg: any, i: number) =>
      typeof seg === 'string' ? seg : <strong key={i}>{seg.text}</strong>
    );
  };

  // Handler para clicar em uma notificação - abre drawer sem navegar
  const handleNotificationClick = (notification: Record<string, any>) => {
    markAsRead(notification.id);

    // Abrir drawer da entidade sem sair da tela atual
    if (notification.entidade_tipo && notification.entidade_id && onOpenDrawer) {
      onOpenDrawer(notification.entidade_tipo, notification.entidade_id);
    }

    setIsOpen(false);
  };

  return (
    <div className="notification-center" style={{ position: 'relative' }}>
      {/* Botão do sino */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        title={unreadCount > 0 ? `${unreadCount} notificações não lidas` : 'Notificações'}
        style={{
          position: 'relative',
          width: isExpanded ? '44px' : '36px',
          height: isExpanded ? '44px' : '36px',
          background: isOpen
            ? 'color-mix(in srgb, var(--sidebar-fg) 15%, transparent)'
            : 'transparent',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--sidebar-fg)',
          filter: 'var(--sidebar-logo-shadow)',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          setIsBellHovered(true);
          if (!isOpen) {
            e.currentTarget.style.background = 'color-mix(in srgb, var(--sidebar-fg) 10%, transparent)';
          }
        }}
        onMouseLeave={(e) => {
          setIsBellHovered(false);
          if (!isOpen) {
            e.currentTarget.style.background = 'transparent';
          }
        }}
      >
        {isBellHovered ? (
          <BellRing size={20} style={{
            animation: 'bellRing 0.5s ease-in-out',
            transformOrigin: 'top center'
          }} />
        ) : (
          <Bell size={20} />
        )}

        {/* Badge de contador */}
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              minWidth: '18px',
              height: '18px',
              padding: '0 4px',
              borderRadius: '9px',
              background: 'var(--color-danger, #ef4444)',
              color: 'white',
              fontSize: '11px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none'
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel - renderizado via Portal */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="notification-panel"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            transform: 'translateY(-100%)',
            background: 'rgba(255, 255, 255, 0.50)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)',
            width: '420px',
            maxHeight: '480px',
            zIndex: 10000,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{
              fontWeight: '600',
              fontSize: '15px',
              color: 'var(--text)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              Central de notificações
              {unreadCount > 0 && (
                <span style={{
                  fontSize: '12px',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  background: '#154680',
                  color: '#fff'
                }}>
                  {unreadCount} nova{unreadCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Lista de notificações */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            maxHeight: '360px'
          }}>
            {loading ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: '14px'
              }}>
                Carregando...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: '14px'
              }}>
                <Bell size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
                <div>Nenhuma notificação</div>
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = NOTIFICATION_ICONS[notification.tipo as keyof typeof NOTIFICATION_ICONS] || Info;
                const color = NOTIFICATION_COLORS[notification.tipo as keyof typeof NOTIFICATION_COLORS] || 'var(--color-primary)';
                const isRead = !!notification.lido_at;

                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer',
                      background: 'transparent',
                      transition: 'background 0.2s ease',
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'flex-start'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {/* Indicador de não lida */}
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: isRead ? 'transparent' : color,
                      flexShrink: 0,
                      marginTop: '6px'
                    }} />

                    {/* Ícone */}
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: `${color}15`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Icon size={16} style={{ color }} />
                    </div>

                    {/* Conteúdo */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: isRead ? '400' : '600',
                        color: '#1a1a1a',
                        marginBottom: '4px',
                        lineHeight: '1.4'
                      }}>
                        {renderMarkdown(notification.titulo)}
                      </div>
                      {notification.mensagem && (
                        <div style={{
                          fontSize: '12px',
                          color: '#444',
                          marginBottom: '4px',
                          lineHeight: '1.4',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {renderMarkdown(notification.mensagem)}
                        </div>
                      )}
                      <div style={{
                        fontSize: '11px',
                        color: 'var(--muted)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span>{formatRelativeTime(notification.created_at)}</span>
                      </div>
                    </div>

                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <button
                onClick={markAllAsRead}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-primary)',
                  fontSize: '13px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <CheckCheck size={16} />
                Marcar todas como lidas
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
      {/* Animação do sino */}
      <style>{`
        @keyframes bellRing {
          0% { transform: rotate(0deg); }
          15% { transform: rotate(14deg); }
          30% { transform: rotate(-12deg); }
          45% { transform: rotate(10deg); }
          60% { transform: rotate(-8deg); }
          75% { transform: rotate(4deg); }
          100% { transform: rotate(0deg); }
        }
      `}</style>
    </div>
  );
}
